import type { Ctx } from '@/core/context';
import { nextRandom } from '@/core/rng';
import { onShift } from '@/core/time';
import { menuToday } from './progress';
import { tr } from '@/i18n';
import { flowBlocked } from './flow';

/**
 * 訂單與產線模型（簡化）：
 * - 每條產線依 ratePerMinute 自動生產；被案件「停線」或「重工中」時不生產
 * - 案件進行中但沒停線 → 這段時間的產品記為「可疑品」；案件壞結局時要重工或報廢
 * - 正式訂單在 game.time.officialOrderAt 進來，數量與預估不同（插單、加量、減量）
 */
export function planOrders(ctx: Ctx): void {
  const { day, c } = ctx;
  const caseLines = day.caseQueue.map(id => c.caseById.get(id)!.line).filter((x): x is string => !!x);
  const pool = c.products.map(p => p.id).filter(id => !caseLines.includes(id));
  const picked = [...new Set(caseLines)];
  while (picked.length < 3 && pool.length) picked.push(pool.splice(Math.floor(nextRandom(day) * pool.length), 1)[0]);
  const minutes = day.shiftEnd - day.shiftStart;
  day.orders.lines = picked.map(id => {
    const p = c.products.find(x => x.id === id)!;
    const forecast = Math.round(p.ratePerMinute * minutes * 0.62 / 10) * 10;
    return { product: id, forecast, target: forecast, produced: 0, suspect: 0, scrapped: 0 };
  });
  for (const p of c.products) day.lines[p.id] = { stoppedBy: null, reworkUntil: 0, alt: false, changeoverUntil: 0, nextChange: day.shiftStart + 60 + Math.floor(nextRandom(day) * 90) };
  const official = onShift(c.game.time.officialOrderAt, day.shiftStart);
  day.orders.official = !(official >= day.shiftStart && official <= day.shiftEnd);
}

export function tickProduction(ctx: Ctx, minutes: number): void {
  const { day, c } = ctx;
  if (day.clock > day.shiftEnd + 180) return;
  const active = ctx.c.caseById.get(day.caseQueue[day.activeCase] ?? '');
  const activeProg = active ? day.cases[active.id] : null;
  for (const l of day.orders.lines) {
    const p = c.products.find(x => x.id === l.product)!;
    const ln = day.lines[l.product];
    if (ln.stoppedBy || day.clock < ln.reworkUntil || day.clock < ln.changeoverUntil) continue;
    if (l.produced >= l.target) continue;
    if (flowBlocked(ctx, l.product)) continue;   // v0.8 線邊缺空籃／收料車滿了
    const made = Math.min(p.ratePerMinute * minutes, l.target - l.produced);
    l.produced += made;
    if (active && active.line === l.product && activeProg && activeProg.stage !== 'pending' && activeProg.stage !== 'done') l.suspect += made;
  }
  // 偶爾換線（輸送帶上的東西會換成另一款，換線期間停 8 分鐘）
  for (const p of c.products) {
    const ln = day.lines[p.id]; if (!ln) continue;
    if (day.clock >= ln.nextChange) {
      ln.alt = !ln.alt; ln.changeoverUntil = day.clock + 8; ln.nextChange = day.clock + 80 + Math.floor(nextRandom(day) * 60);
      const pair = menuToday(ctx, p.id); const nm = pair ? ctx.c.menuById.get(pair[ln.alt ? 1 : 0])?.name : (ln.alt ? p.alt.name : p.name);
      if (p.floor === day.floor) ctx.ui.toast(tr("🔄 {0} 換線：{1}（標籤、包材、食材核對；換好後站在線邊可以記下這道食譜）", p.floor, nm));
    }
  }
  // 正式訂單
  const official = onShift(c.game.time.officialOrderAt, day.shiftStart);
  if (!day.orders.official && day.clock >= official) {
    day.orders.official = true;
    const notes: string[] = [];
    for (const l of day.orders.lines) {
      const k = 0.85 + nextRandom(day) * 0.45;
      const t = Math.round(l.forecast * k / 10) * 10;
      notes.push(`${c.products.find(p => p.id === l.product)!.name} ${l.forecast}→${t}`);
      l.target = t;
    }
    ctx.ui.toast(tr("📨 正式訂單進來了：{0}", notes.join(tr("、"))));
    ctx.log(tr("正式訂單傳入：{0}", notes.join(tr("、"))), 'warn');
    ctx.day.player.stress = Math.min(100, ctx.day.player.stress + 6);
  }
}

export function completion(ctx: Ctx): number {
  const ls = ctx.day.orders.lines;
  const t = ls.reduce((s, l) => s + l.target, 0);
  return t ? ls.reduce((s, l) => s + Math.max(0, l.produced - l.scrapped), 0) / t : 1;
}

/** 案件結束時處理可疑品 */
export function settleSuspect(ctx: Ctx, line: string | null, grade: 'good' | 'bad', scrapPercent = 0, reworkMinutes = 0): string | null {
  if (!line) return null;
  const l = ctx.day.orders.lines.find(x => x.product === line); if (!l) return null;
  const p = ctx.c.products.find(x => x.id === line)!;
  const sus = Math.round(l.suspect); l.suspect = 0;
  ctx.day.lines[line].stoppedBy = null;
  if (grade === 'good' || sus <= 0) return null;
  if (scrapPercent) {
    const scrap = Math.min(l.produced, Math.max(Math.round(sus * 0.6), Math.round(l.produced * scrapPercent / 100)));
    l.scrapped += scrap;
    return tr("{0}：{1}{2} 無法確認安全，填報廢單", p.name, scrap, p.unit);
  }
  if (reworkMinutes) {
    const mins = Math.round(Math.max(reworkMinutes, (sus / p.ratePerMinute) * 1.5));
    ctx.day.lines[line].reworkUntil = ctx.day.clock + mins;
    return tr("{0}：{1}{2} 需要隔離重工，產線佔用 {3} 分鐘（約產能 1.5 倍）", p.name, sus, p.unit, mins);
  }
  return null;
}
